import {
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReuseModal } from "../../ReuseModal";
import { CustomButton } from "../../CustomButton";
import { ReuseTooltip } from "../../ReuseTooltip";
import { useParams } from "react-router-dom";
import {
  UpdateUserRoleRequest,
  UserThatShareCampaign,
} from "../../../../types/apiTypes";
import React, { useEffect, useState } from "react";
import { SharedUser } from "./SharedUser";
import { SharedUserRow } from "./SharedUserRow";
import { useAppDispatch } from "../../../../hooks";
import {
  shareCampaignApi,
  useInviteUserToShareCampaignMutation,
} from "../../../../api";
import { ShareCampaignRoles } from "../../../../types/enums";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { EMAIL_REGEX } from "../../../../utils/regex";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isLoading: boolean;
  sharedCampaignUsers: UserThatShareCampaign[];
  changeUsersRoles: (users: UpdateUserRoleRequest) => void;
  userEmail: string;
};

export const ShareCampaignModal = ({
  isOpen,
  setIsOpen,
  isLoading,
  sharedCampaignUsers,
  changeUsersRoles,
  userEmail,
}: Props) => {
  const { campaign_id } = useParams();
  const dispatch = useAppDispatch();

  const [sharedUsers, setSharedUsers] =
    useState<UserThatShareCampaign[]>(sharedCampaignUsers);
  const [searchValue, setSearchValue] = useState<string>("");

  const checkChanges = () => {
    if (sharedCampaignUsers.length !== sharedUsers.length) return false;

    let isSame = true;
    for (let i = 0; i < sharedCampaignUsers.length; i++) {
      if (!isSame) break;

      isSame =
        sharedCampaignUsers[i].roles.length === sharedUsers[i].roles.length &&
        sharedCampaignUsers[i].roles.every((role) =>
          sharedUsers[i].roles.includes(role),
        );
    }

    return isSame;
  };

  const isChanged = checkChanges();

  const [inviteUser, { isLoading: isInviteUserLoading }] =
    useInviteUserToShareCampaignMutation();

  useEffect(() => {
    setSharedUsers(sharedCampaignUsers);
  }, [sharedCampaignUsers]);

  const submitHandler = () => {
    changeUsersRoles({
      campaign_id: campaign_id!,
      users_roles: sharedUsers.map(({ email, roles }) => ({
        user_email: email,
        roles,
      })),
    });
  };

  const searchUserHandler = (value: string) => {
    setSearchValue(value);
  };

  const inviteHandler = () => {
    if (!searchValue || !EMAIL_REGEX.test(searchValue)) {
      toast.error(
        <CustomToast
          type="error"
          text={"Please enter a valid email address"}
          title="Failed to Invite User"
        />,
      );

      return;
    }

    inviteUser({ campaign_id: campaign_id!, user_email: searchValue })
      .unwrap()
      .then(() => {
        toast.success(
          <CustomToast
            type="success"
            text="User successfully invited to campaign with viewer role"
            title="User Successfully Invited"
          />,
        );

        dispatch(shareCampaignApi.util.invalidateTags(["shared-users"]));
        setSearchValue("");
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type="error"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong during processing. Please try again"
            }
            title="Failed to Invite User"
          />,
        );
      });
  };

  const isInviteLimit = sharedUsers.length >= 10;

  const changeRoleHandler = (email: string, role: ShareCampaignRoles) => {
    if (role === ShareCampaignRoles.editor) {
      setSharedUsers((prev) =>
        prev.map((item) => {
          if (item.email === email) {
            return {
              ...item,
              roles: [...item.roles, ShareCampaignRoles.editor],
            };
          }

          return {
            ...item,
            roles: item.roles.filter((r) => r !== ShareCampaignRoles.editor),
          };
        }),
      );
    }

    if (role === ShareCampaignRoles.admin) {
      setSharedUsers((prev) =>
        prev.map((item) => {
          if (item.email === email) {
            return {
              ...item,
              roles: item.roles.includes(ShareCampaignRoles.admin)
                ? item.roles.filter((r) => r !== ShareCampaignRoles.admin)
                : [...item.roles, ShareCampaignRoles.admin],
            };
          }

          return item;
        }),
      );
    }
  };

  return (
    <ReuseModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Share this campaign"
      maxWidth="540px"
      submitBtnText="Save"
      submitBtnColor="#1C3C6C"
      onSubmit={submitHandler}
      isLoading={isLoading}
      isDisabled={isLoading || isChanged}
      isDisabledCancelBtn={isLoading}
    >
      <Stack gap="16px">
        <Stack gap="24px">
          <Typography component="span" variant="body1" color="text.secondary">
            Send an invitation to a team member to collaborate on this campaign.
            There can be only one editor at a time, but multiple viewers and
            admins.
          </Typography>
          <Stack flexDirection="row" gap="8px" width="100%">
            <TextField
              variant="outlined"
              value={searchValue}
              placeholder="Enter email address"
              sx={{ width: "100%" }}
              onChange={(e) => {
                searchUserHandler(e.target.value);
              }}
              disabled={isInviteUserLoading}
            />

            <ReuseTooltip
              text={
                isInviteLimit
                  ? "You’ve reached the maximum limit of 10 users."
                  : ""
              }
              hideChildren={false}
              maxWidth="200px"
            >
              <CustomButton
                onClick={inviteHandler}
                variant="contained"
                customColor="#1C3C6C"
                type="button"
                startIcon={
                  isInviteUserLoading && (
                    <CircularProgress size={24} sx={{ color: "inherit" }} />
                  )
                }
                disabled={isInviteUserLoading || isInviteLimit}
                sx={{ height: "40px" }}
              >
                {isInviteLimit
                  ? "Limit reached"
                  : isInviteUserLoading
                    ? "Invite"
                    : "Invite User"}
              </CustomButton>
            </ReuseTooltip>
          </Stack>
          <Box>
            <SharedUserRow
              nameBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Name
                </Typography>
              }
              editorBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Editor
                </Typography>
              }
              adminBlock={
                <Typography variant="button" sx={{ color: "#232323" }}>
                  Admin
                </Typography>
              }
              menuBlock={<Box />}
              withBg={true}
            />
            <Stack
              sx={{
                overflowY: "auto",
                flexGrow: 1,
                maxHeight: "348px",
              }}
            >
              {sharedUsers.map((user) => (
                <SharedUser
                  campaign_id={campaign_id!}
                  key={user.email}
                  user={user}
                  changeRoleHandler={changeRoleHandler}
                  userEmail={userEmail}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
